import { useMemo } from 'react';

import { ClientShortProfile } from '@ndvr/customer-api';

import { filterByDateRange } from 'utils';

import { ClientFilters, useClientFiltersContext } from './ClientFiltersContext';

const filterByPersonOrgType = (client: ClientShortProfile, filters: ClientFilters) =>
    filters.personOrgTypes.includes(client.personOrgType);

const filterByStates = (client: ClientShortProfile, filters: ClientFilters) =>
    filters.states.includes(client.primaryAddress?.state || '');

const filterByStartDate = (client: ClientShortProfile, filters: ClientFilters): boolean =>
    filterByDateRange(client.startDate, filters.startDateStart, filters.startDateEnd);

export const useClientFilters = (clients: ClientShortProfile[]) => {
    const { filters, isPersonOrgTypeFilterActive, isStatesFilterActive, isStartDateFilterActive } =
        useClientFiltersContext();

    const activeFilters = useMemo(
        () =>
            [
                {
                    isActive: isPersonOrgTypeFilterActive,
                    filterFunction: (client: ClientShortProfile) => filterByPersonOrgType(client, filters),
                },
                {
                    isActive: isStatesFilterActive,
                    filterFunction: (client: ClientShortProfile) => filterByStates(client, filters),
                },
                {
                    isActive: isStartDateFilterActive,
                    filterFunction: (client: ClientShortProfile) => filterByStartDate(client, filters),
                },
            ].filter(({ isActive }) => isActive),
        [filters, isPersonOrgTypeFilterActive, isStatesFilterActive, isStartDateFilterActive],
    );

    const filteredClients = useMemo(
        () => clients.filter((client) => activeFilters.every(({ filterFunction }) => filterFunction(client))),
        [clients, activeFilters],
    );

    return {
        data: filteredClients,
    };
};
